import {CssTheme} from '@ui/themes/css-theme';
import {themeEl} from '@ui/root-el';
import {useSettings} from '@ui/settings/use-settings';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {usePreferredColorScheme} from '@ui/themes/use-preferred-color-scheme';
import {getCurrentThemeValue} from '@ui/themes/utils/get-current-theme-value';
import {useCookie} from '@ui/utils/hooks/use-cookie';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

type ColorScheme = 'dark' | 'light' | 'system';
const THEME_COOKIE_DAYS = 365;
const RADIUS_CLASSES = [
  'radius-none',
  'radius-sm',
  'radius-md',
  'radius-default',
] as const;

export interface ColorSchemeContextValue {
  colorScheme: Omit<ColorScheme, 'system'>;
  setColorScheme: (colorScheme: ColorScheme) => void;
  themes: CssTheme[];
  selectedThemeId: number | null;
  setTheme: (theme: CssTheme) => void;
}

export const ColorSchemeContext = createContext<ColorSchemeContextValue>(
  null!,
);

export function ColorSchemeProvider({
  children,
  cookieName,
}: {
  children: any;
  cookieName?: string;
}) {
  const {themes: themeSettings} = useSettings();
  const allThemes = useMemo(
    () => getBootstrapData().themes ?? [],
    // bootstrap themes are static for the session
    [],
  );
  const [appliedScheme, setAppliedScheme] = useState<
    Omit<ColorScheme, 'system'>
  >(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );
  const adminSelectedDefaultScheme: ColorScheme =
    themeSettings?.default_scheme ?? 'system';
  const userPreferredColorScheme = usePreferredColorScheme();
  const defaultScheme: Omit<ColorScheme, 'system'> =
    adminSelectedDefaultScheme === 'system'
      ? userPreferredColorScheme
      : adminSelectedDefaultScheme;
  const [schemeCookie, setCurrentSchemeCookie] = useCookie(
    cookieName ?? 'be-color-scheme',
  );
  const [themeIdCookie, setThemeIdCookie] = useCookie('be-active-theme');
  const selectedScheme =
    schemeCookie === 'light' || schemeCookie === 'dark'
      ? schemeCookie
      : defaultScheme;

  const selectedThemeId = useMemo(() => {
    const fromCookie = themeIdCookie ? Number(themeIdCookie) : NaN;
    if (!Number.isNaN(fromCookie) && allThemes.some(t => t.id === fromCookie)) {
      return fromCookie;
    }
    const fallback = allThemes.find(t =>
      selectedScheme === 'dark' ? t.default_dark : t.default_light,
    );
    return fallback?.id ?? allThemes[0]?.id ?? null;
  }, [allThemes, selectedScheme, themeIdCookie]);

  const applyTheme = useCallback((theme: CssTheme) => {
    applyCssThemeToDom(theme);
  }, []);

  const setColorScheme = useCallback(
    (scheme: ColorScheme) => {
      setCurrentSchemeCookie(scheme, {days: THEME_COOKIE_DAYS});
      const colorScheme =
        scheme === 'light' || scheme === 'dark'
          ? scheme
          : userPreferredColorScheme;
      const matchingTheme = allThemes.find(t =>
        colorScheme === 'dark' ? t.default_dark : t.default_light,
      );
      if (matchingTheme) {
        setThemeIdCookie(String(matchingTheme.id), {days: THEME_COOKIE_DAYS});
        applyTheme(matchingTheme);
        setAppliedScheme(matchingTheme.is_dark ? 'dark' : 'light');
        return;
      }
      setAppliedScheme(colorScheme);
      applyColorSchemeToDom(colorScheme);
    },
    [
      allThemes,
      applyTheme,
      setCurrentSchemeCookie,
      setThemeIdCookie,
      userPreferredColorScheme,
    ],
  );

  const setTheme = useCallback(
    (theme: CssTheme) => {
      setThemeIdCookie(String(theme.id), {days: THEME_COOKIE_DAYS});
      setCurrentSchemeCookie(theme.is_dark ? 'dark' : 'light', {
        days: THEME_COOKIE_DAYS,
      });
      applyTheme(theme);
      setAppliedScheme(theme.is_dark ? 'dark' : 'light');
    },
    [applyTheme, setCurrentSchemeCookie, setThemeIdCookie],
  );

  useEffect(() => {
    const selected = allThemes.find(t => t.id === selectedThemeId);
    if (selected) {
      applyTheme(selected);
      setAppliedScheme(selected.is_dark ? 'dark' : 'light');
      return;
    }
    if (appliedScheme !== selectedScheme) {
      setAppliedScheme(selectedScheme);
      applyColorSchemeToDom(selectedScheme);
    }
  }, [allThemes, applyTheme, selectedScheme, selectedThemeId]);

  const contextValue: ColorSchemeContextValue = useMemo(() => {
    return {
      colorScheme: appliedScheme,
      setColorScheme,
      themes: allThemes,
      selectedThemeId,
      setTheme,
    };
  }, [allThemes, appliedScheme, selectedThemeId, setColorScheme, setTheme]);

  return (
    <ColorSchemeContext.Provider value={contextValue}>
      {children}
    </ColorSchemeContext.Provider>
  );
}

export function applyCssThemeToDom(theme: CssTheme) {
  applyColorSchemeToDom(theme.is_dark ? 'dark' : 'light');

  Object.entries(theme.values ?? {}).forEach(([name, value]) => {
    themeEl.style.setProperty(name, value);
  });

  const fontFamily = theme.font?.family;
  if (fontFamily) {
    themeEl.style.setProperty('--be-font-family', fontFamily);
  }

  themeEl.classList.remove(...RADIUS_CLASSES);
  themeEl.classList.add(`radius-${theme.radius ?? 'default'}`);

  const themeColorMetaEl = document.querySelector('meta[name="theme-color"]');
  if (themeColorMetaEl) {
    themeColorMetaEl.setAttribute(
      'content',
      theme.values?.['--be-background'] ??
        getCurrentThemeValue('--be-background'),
    );
  }
}

export function applyColorSchemeToDom(scheme: Omit<ColorScheme, 'system'>) {
  if (scheme === 'dark') {
    themeEl.classList.add('dark');
    themeEl.classList.remove('light');
  } else {
    themeEl.classList.remove('dark');
    themeEl.classList.add('light');
  }

  const themeColorMetaEl = document.querySelector('meta[name="theme-color"]');
  if (themeColorMetaEl) {
    themeColorMetaEl.setAttribute(
      'content',
      getCurrentThemeValue('--be-background'),
    );
  }
}
